"use client";

import { useEffect, useReducer, useState } from "react";
import { useStores } from "hooks";
import { useCompatQueryParams } from "domain/Routing/hooks/useCompatRouter";
import { RecordCheckErrorMessages } from "utils/pricing-response/RecordCheckErrorMessages";
import { dataLayerEvents } from "domain/SeoAndAnalytics/GoogleAnalytics/dataLayerEvents";
import { VinregSearchType } from "domain/SeoAndAnalytics/GoogleAnalytics/dataLayerEvents.types";
import { capitalizeVehicleText, normalizeModel } from "../utils/formatVehicleText";
import { useTextLinkParams, TextLinkParams, getStoredKlinkId } from "utils/text-link/TextLinkParamsContext";
import { PartnerParams } from "utils/pricing-response/pricing-api";
import { additionalContentReducer } from "../additionalContentReducer";
import RecaptchaActions from "domain/Recaptcha/actions";
//ABTest CG-45
import { usePackagesVariant } from "hooks/optimizelyTests";
//End ABTest CG-45

const buildPartnerParams = (textLinkParams: TextLinkParams | null): PartnerParams | undefined => {
  const utmSource = (textLinkParams?.utmSource ?? textLinkParams?.utm_source) as string | undefined;
  const adId = (textLinkParams?.adId ?? textLinkParams?.adid) as string | undefined;
  const klinkId = getStoredKlinkId();

  if (!utmSource && !adId && !klinkId) {
    return undefined;
  }

  return {
    ...(utmSource && { utmSource }),
    ...(adId && { adId }),
    ...(klinkId && { klinkId }),
  };
};

/**
 * The encoded vinreg comes to CARFAX as a URL which is base64 encoded. Base64 allows slash character which has a special
 * meaning when calling other REST services - a resource separator. Thus, before sending it further, it must be changed to something else
 * (underscore in this case) and later converted back, otherwise it can not be used as a path parameter.
 *
 * @param vinregEncoded
 */
function preParse(vinregEncoded: string): string {
  const SLASH = "/";
  const UNDERSCORE = "_";
  return vinregEncoded.replace(SLASH, UNDERSCORE);
}

export interface UsePartnerRecordCheckReturn {
  isLoading: boolean;
  isVinValid: boolean;
  showContent: boolean;
  vin: string | undefined;
  makeAndModel: { make: string; model: string };
  encryptedVin: string | undefined;
  totalRecords: number;
  vhrAlreadyBought: boolean;
}

export const usePartnerRecordCheck = (): UsePartnerRecordCheckReturn => {
  const { reportPricingStore, recordCheckStore, checkoutStore } = useStores();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryParams = useCompatQueryParams();
  const textLinkParams = useTextLinkParams();
  //ABTest CG-45
  const { variant } = usePackagesVariant();
  //End ABTest CG-45

  const { make, model, vin, plate_encoded } = {
    ...(queryParams as {
      make?: string;
      model?: string;
      vin?: string;
      plate_encoded?: string;
    }),
    ...textLinkParams,
  };

  const [additionalContent, dispatch] = useReducer(additionalContentReducer, {
    showContent: false,
    isVinValid: false,
    totalRecords: 0,
    make: capitalizeVehicleText(make),
    model: normalizeModel(make, model),
    vhrAlreadyBought: false,
  });

  const handleErrorPricingResponse = (e, vinQuery) => {
    if (e?.key === RecordCheckErrorMessages.REPORT_ALREADY_BOUGHT) {
      dispatch({ type: "updateVhrAlreadyBought" });
      dispatch({ type: "updateEncryptedVin", encryptedVin: vinQuery });
      setIsLoading(false);
      dispatch({ type: "vinValidation", isVinValid: true });
      dispatch({ type: "showContent" });
    } else if (e?.key !== RecordCheckErrorMessages.RECAPTCHA) {
      setIsLoading(false);
      dispatch({ type: "vinValidation", isVinValid: false });
      dispatch({ type: "showContent" });
      dataLayerEvents.vinSearchErrorEvent(VinregSearchType.PARTNER);
    }
  };

  const handlePricingResponse = async (vinQuery, plateEncodedQuery) => {
    setIsLoading(true);
    const PricingApi = (await import("utils/pricing-response/pricing-api")).default;
    const partnerParams = buildPartnerParams(textLinkParams);
    try {
      //ABTest CG-45 (variant param)
      const data = plateEncodedQuery
        ? await PricingApi.fetchPricingResponseForEncryptedVin(
            preParse(plateEncodedQuery),
            RecaptchaActions.partner,
            partnerParams,
            variant,
          )
        : await PricingApi.fetchPricingResponseForVin(vinQuery, RecaptchaActions.partner, partnerParams, variant);
      //End ABTest CG-45

      reportPricingStore.setDataFromApi(data);
      recordCheckStore.setDataFromApi({
        ...data,
        encryptedQuery: plateEncodedQuery || undefined,
      });
      checkoutStore.setDataFromApi(data);

      const totalRecords = recordCheckStore.reportDataTotal;

      dispatch({ type: "updateEncryptedVin", encryptedVin: data.recordCheck.query });
      dispatch({ type: "updateMake", make: capitalizeVehicleText(recordCheckStore.make) });
      dispatch({ type: "updateModel", model: normalizeModel(recordCheckStore.make, recordCheckStore.model) });
      dispatch({ type: "setTotalRecords", totalRecords });
      setIsLoading(false);
      dispatch({ type: "vinValidation", isVinValid: true });
      dispatch({ type: "showContent" });
      recordCheckStore.setVinregSearchType(VinregSearchType.PARTNER);
      dataLayerEvents.vinSearchSuccessEvent(recordCheckStore.trackingData, VinregSearchType.PARTNER);
    } catch (error) {
      handleErrorPricingResponse(error, vinQuery);
    }
  };

  useEffect(() => {
    if (vin?.trim() || plate_encoded?.trim()) {
      handlePricingResponse(vin, plate_encoded);
    } else {
      recordCheckStore.clearUuid();
      dispatch({ type: "showContent" });
    }
  }, []);

  return {
    isLoading,
    isVinValid: additionalContent.isVinValid,
    showContent: additionalContent.showContent,
    vin,
    makeAndModel: { make: additionalContent.make, model: additionalContent.model },
    encryptedVin: additionalContent.encryptedVin,
    totalRecords: additionalContent.totalRecords,
    vhrAlreadyBought: additionalContent.vhrAlreadyBought,
  };
};
